import { isValidTime } from './types';
import type { DocketSession, TakenBy } from './types';

export type ClaimCode =
  | 'TAKEN' | 'DONE' | 'DROPPED' | 'WAITS_NICK' | 'NOT_YET' | 'BLOCKED' | 'STALE_ROW' | 'NOT_FOUND' | 'BAD_INPUT' | 'NOT_CLAIMABLE';

export type ClaimResult =
  | { readonly ok: true; readonly session: DocketSession; readonly alreadyHeld: boolean }
  | { readonly ok: false; readonly code: ClaimCode; readonly reason: string };

type Blocker = { readonly code: ClaimCode; readonly reason: string };
const no = (code: ClaimCode, reason: string): Blocker => ({ code, reason });

/**
 * Why a row cannot be claimed right now, or null. One rule for the claim AND the ranking, so
 * "available" in a list can never disagree with what a claim does. Allow-by-rule: only READY
 * (or WAITS-TIME whose time has passed) can pass; every other or unknown status is refused.
 */
/**
 * The list is the truth. The caller's row is only a handle: its id picks the list's copy and its
 * status says what the caller last saw. Everything else (dependencies, times, holder, title) is read
 * from the list's copy, so a doctored or stripped row cannot change the outcome (RVW-OH13).
 */
function resolve(row: DocketSession, all: readonly DocketSession[], nowIso: string): { readonly listed: DocketSession } | { readonly blocker: Blocker } {
  if (!isValidTime(nowIso)) return { blocker: no('BAD_INPUT', `now "${String(nowIso)}" is not a date-time`) };
  const seen = new Set<string>();
  for (const r of all) {
    if (seen.has(r.id)) return { blocker: no('BAD_INPUT', `duplicate session id ${r.id}`) };
    seen.add(r.id);
  }
  const listed = all.find((r) => r.id === row.id);
  if (!listed) return { blocker: no('NOT_FOUND', `no session ${String(row.id)} in the list`) };
  if (listed.status !== row.status) {
    return { blocker: no('STALE_ROW', `row ${row.id} is ${row.status} here but ${listed.status} in the list: re-read it`) };
  }
  return { listed };
}

const copy = (row: DocketSession): DocketSession => ({
  ...row,
  dependsOn: [...row.dependsOn],
  ...(row.takenBy ? { takenBy: { ...row.takenBy } } : {}),
  ...(row.result ? { result: { ...row.result, prs: [...row.result.prs] } } : {}),
});

export function claimBlocker(caller: DocketSession, all: readonly DocketSession[], nowIso: string): Blocker | null {
  const r = resolve(caller, all, nowIso);
  if ('blocker' in r) return r.blocker;
  return blockerOf(r.listed, all, nowIso);
}

function blockerOf(row: DocketSession, all: readonly DocketSession[], nowIso: string): Blocker | null {
  switch (row.status) {
    case 'TAKEN': {
      const h = row.takenBy;
      return no('TAKEN', `already taken by ${h?.dev ?? 'unknown'}${h?.window ? ` (${h.window})` : ''} at ${h?.at ?? 'unknown time'}`);
    }
    case 'DONE': return no('DONE', 'row is DONE');
    case 'DROPPED': return no('DROPPED', 'row is DROPPED');
    case 'WAITS-NICK': return no('WAITS_NICK', 'row waits on Nick');
    case 'WAITS-TIME':
      if (!isValidTime(row.earliestAt)) return no('BAD_INPUT', 'WAITS-TIME row has no valid earliestAt');
      break;
    case 'READY': break;
    default: return no('NOT_CLAIMABLE', `status "${String(row.status)}" is not claimable`);
  }
  if (row.earliestAt !== undefined) {
    if (!isValidTime(row.earliestAt)) return no('BAD_INPUT', `earliestAt "${row.earliestAt}" is not a date-time`);
    if (Date.parse(row.earliestAt) > Date.parse(nowIso)) return no('NOT_YET', `not before ${row.earliestAt}`);
  }
  for (const dep of row.dependsOn) {
    const d = all.find((r) => r.id === dep);
    if (!d) return no('BLOCKED', `depends on ${dep}, which does not exist`);
    if (d.status !== 'DONE') return no('BLOCKED', `depends on ${dep} (${d.status})`);
  }
  return null;
}

/**
 * The claim rule, pure. The server runs it INSIDE a database transaction on the freshly read
 * row, so a second concurrent claimer re-reads a TAKEN row and gets code TAKEN.
 * A retry by the same holder succeeds with alreadyHeld, so a claim that landed but reported
 * failure can be repeated without looking like a loss.
 */
export function claimSession(
  caller: DocketSession,
  all: readonly DocketSession[],
  claimer: { readonly dev: string; readonly window?: string },
  nowIso: string,
): ClaimResult {
  if (typeof claimer.dev !== 'string' || claimer.dev.trim() === '') return { ok: false, code: 'BAD_INPUT', reason: 'claimer has no name' };
  const r = resolve(caller, all, nowIso);
  if ('blocker' in r) return { ok: false, ...r.blocker };
  const row = r.listed;
  const h = row.takenBy;
  if (row.status === 'TAKEN' && h && h.dev === claimer.dev && (h.window ?? '') === (claimer.window ?? '')) {
    return { ok: true, alreadyHeld: true, session: copy(row) };
  }
  const blocked = blockerOf(row, all, nowIso);
  if (blocked) return { ok: false, ...blocked };
  const takenBy: TakenBy = { dev: claimer.dev, ...(claimer.window ? { window: claimer.window } : {}), at: nowIso };
  return {
    ok: true,
    alreadyHeld: false,
    session: { ...copy(row), status: 'TAKEN', takenBy },
  };
}
